import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Img from 'react-image';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { isToday } from 'date-fns';
import get from 'lodash.get';
import hash from 'object-hash';
import { Box, FlexBox, Typography, Card, useMediaQuery, Grid, useTheme, MuiBox } from '@kis-technologies/kis-ui';
import EmptyTicketIcon from 'assets/tickets_icon.png';
import WillCallLogo from 'assets/will_call_icon.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { Paginator } from 'components/Paginator';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, actions as cartActions, Item } from 'services/cart';
import {
  selectors as configurationSelectors,
  Configuration,
  EventDisplaySettings,
  Messages
} from 'services/configuration';
import { selectors as mediaSelectors, MediaResponse } from 'services/media';
import { actions as ticketingActions, selectors as ticketingSelectors, Event, EventDetails } from 'services/ticketing';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import { useStyles } from './styled';

const UpsellSelectionView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Action Creators and Selectors
  const {
    getEvents,
    setEventsConfig,
    resetUpsellProducts,
    setEventDetails,
    resetEventDetails,
    setMembershipProduct,
    resetMembershipProduct,
    setTiersConfig,
    setUpsell,
    removeTicketsFromCart,
    releaseCart,
    resetCartError
  } = useActions({
    getEvents: ticketingActions.getEvents,
    setEventsConfig: ticketingActions.setEventsConfig,
    resetUpsellProducts: ticketingActions.resetUpsellProducts,
    setEventDetails: ticketingActions.setEventDetails,
    resetEventDetails: ticketingActions.resetEventDetails,
    setMembershipProduct: ticketingActions.setMembershipProduct,
    resetMembershipProduct: ticketingActions.resetMembershipProduct,
    setTiersConfig: ticketingActions.setTiersConfig,
    setUpsell: ticketingActions.setUpsell,
    removeTicketsFromCart: cartActions.removeTicketsFromCart,
    releaseCart: cartActions.releaseCart,
    resetCartError: cartActions.resetCartError
  });
  const configuration = useSelector(configurationSelectors.selectConfiguration) as Configuration;
  const eventImages = useSelector(mediaSelectors.selectEventImages) || [];
  const events = useSelector(ticketingSelectors.selectEvents);
  const eventDetails = useSelector(ticketingSelectors.selectEventDetails) as EventDetails;
  const membershipProduct = useSelector(ticketingSelectors.selectMembershipProduct);
  const upsellProducts = useSelector(ticketingSelectors.selectUpsellProducts);
  const eventsConfig = useSelector(ticketingSelectors.selectEventsConfig);
  const tiersConfig = useSelector(ticketingSelectors.selectTiersConfig);
  const loading = useSelector(ticketingSelectors.selectLoading);
  const fetchCompleted = useSelector(ticketingSelectors.selectFetchCompleted);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const upsell = useSelector(ticketingSelectors.selectUpsell);
  const cart = useSelector(cartSelectors.selectCart);
  const cartItemIds = useSelector(cartSelectors.selectCartItemIds);
  const cartLoading = useSelector(cartSelectors.selectLoading);
  const cartError = useSelector(cartSelectors.selectError);
  const membershipProductInCart = useSelector(cartSelectors.selectMembershipProductInCart);

  const clientId: string = get(configuration, 'clientId', '');
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const cartLabel = get(configuration, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const todayEventFlag = get(configuration, `${ticketingPath}.todayEventFlag`);
  const eventDisplaySettingsList: EventDisplaySettings[] = get(
    configuration,
    `${ticketingPath}.eventDisplaySettingsList`,
    []
  );
  const cartEnabled = get(configuration, `${ticketingPath}.cart.enabled`, false);
  const productAvailabilityAndSelection = get(configuration, `${ticketingPath}.productAvailabilityAndSelection`, {});
  const messages: Messages = get(productAvailabilityAndSelection, 'upsell.productSelectionMessages', {});
  const header = get(messages, `header.${i18n.language}`, t('upsellSelection.header'));
  const subheader = get(messages, `subheader.${i18n.language}`, t('upsellSelection.subheader', { cartLabel }));
  const backButton = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const upsellPrompt = get(productAvailabilityAndSelection, 'upsellPrompt', {});
  const upsellPromptEnabled: boolean = get(upsellPrompt, 'enabled', false);
  const productsToInitiateUpsellPrompt: string[] = get(upsellPrompt, 'productsToInitiateUpsell', []);
  const numberOfItemsToInitiateUpsellPrompt: number = get(upsellPrompt, 'numberOfItemsToInitiateUpsell', 0);
  const upsellPromptProductName: string = get(upsellPrompt, 'productToUpsell', '');
  const upsellPromptMessages: Messages = get(upsellPrompt, 'messages', {});
  const upsellPromptHeader = get(upsellPromptMessages, `header.${i18n.language}`, t('upsellSelection.promptHeader'));
  const upsellPromptSubheader = get(
    upsellPromptMessages,
    `subheader.${i18n.language}`,
    t('upsellSelection.promptSubheader')
  );
  const upsellPromptCancelButton = get(upsellPromptMessages, `buttons.${i18n.language}[0]`, t('commonText.no'));
  const upsellPromptConfirmButton = get(upsellPromptMessages, `buttons.${i18n.language}[1]`, t('commonText.yes'));

  const [filteredProducts, setFilteredProducts] = React.useState<Event[]>([]);
  const [selectedProduct, setSelectedProduct] = React.useState<string>('');
  const [currentPage, setCurrentPage] = React.useState(eventsConfig.page);
  const [uiLoading, setUILoading] = React.useState(true);
  const [navigateBack, setNavigateBack] = React.useState(false);
  const [showUpsellPrompt, setShowUpsellPrompt] = React.useState<boolean>(false);

  const classes = useStyles();
  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.only('lg'));
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/event_images/`;
  const previousPage = (location.state?.from as string) || '';
  const followingPages = ['/ticketing/tiers', '/ticketing/membership-level-selection', '/ticketing/donation'];
  const isPromptProductStored = Boolean(upsell.promptProduct.details || upsell.promptProduct.membership);

  useEffect(() => {
    if (!upsell.initialProduct.details) {
      // Navigated here from the initial product
      const shouldUpsellPromptBeShown = isUpsellPromptTriggered();

      if (shouldUpsellPromptBeShown || (eventDetails && eventDetails.upsell)) {
        saveInitialProductSelections();
      }

      if (shouldUpsellPromptBeShown) {
        setShowUpsellPrompt(true);
        setUILoading(false);
      } else {
        getUpsellEventsOrProceed();
      }
    } else if (!isPromptProductStored && !upsellProducts.length) {
      // Navigated here from the prompt product
      if (followingPages.includes(previousPage)) {
        // Navigated back from the prompt product, so keep going back before the prompt
        setUpsell({ ...upsell, promptProduct: {} });
        onPrevPageClick();
      } else {
        // Navigated here after adding the prompt product
        savePromptProductSelections();
        getUpsellEventsOrProceed();
      }
    }
  }, []);

  // On fetch of upsell products: show the products, or auto select if 1, or go to the cart
  useEffect(() => {
    if (fetchCompleted.upsellProducts) {
      let filteredUpsellProducts = [...upsellProducts];
      if (membershipProductInCart) {
        filteredUpsellProducts = filteredUpsellProducts.filter((p) => p.type !== 'membership');
      }

      // Only auto select if there is 1 product before and after filtering
      if (upsellProducts.length === 1 && filteredUpsellProducts.length === 1) {
        if (followingPages.includes(previousPage)) {
          onPrevPageClick();
        } else if (upsell.singleUpsellProductOffered) {
          proceedToCart();
        } else {
          setSelectedProduct(upsellProducts[0].id);
        }
      } else if (filteredUpsellProducts.length > 0) {
        setFilteredProducts(filteredUpsellProducts);
        setUILoading(false);
      } else {
        proceedToCart();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsellProducts, fetchCompleted.upsellProducts]);

  // On selection of a product: go to the next page
  useEffect(() => {
    if (selectedProduct !== '') {
      onNextPageClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  // Back navigation after the remove or release API completes
  useEffect(() => {
    if (navigateBack && !cartLoading && !cartError) {
      resetUpsellProducts();

      if (isPromptProductStored) {
        revertToPromptProductSelections();
      } else {
        revertToInitialProductSelections();
      }

      const prevPage = upsell.promptProduct.membership
        ? '/ticketing/membership-data-collection'
        : '/ticketing/selection';
      navigate(prevPage, { state: { from: location.pathname } });
    }
  }, [cartLoading, navigateBack]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  /**
   * Determine if the upsell prompt should be shown based on these conditions:
   * - The upsell prompt is enabled in the configuration
   * - The prompt product is available and not already in the cart
   * - The number of tickets to initiate the prompt was crossed (and not crossed before)
   */
  const isUpsellPromptTriggered = () => {
    // Return false if it is not enabled from the configuration, or the product is not available
    if (
      !upsellPromptEnabled ||
      !eventDetails ||
      !productsToInitiateUpsellPrompt.includes(eventDetails.name) ||
      !upsellPromptProductName ||
      !events.find((event) => event.name === upsellPromptProductName)
    ) {
      return false;
    }

    // Return false if the upsell product is already in the cart
    // Account for membership products being named like "{Product Name}: {Level Name}" in the cart
    if (
      cart &&
      cart.products &&
      cart.products.some((cartProduct) =>
        cartProduct.type === 'membership'
          ? cartProduct.name.includes(upsellPromptProductName)
          : cartProduct.name === upsellPromptProductName
      )
    ) {
      return false;
    }

    // Return false if the number of tickets to initiate the prompt was already crossed
    if (
      upsell.initialProduct.itemQtyInCartBeforeSelection &&
      upsell.initialProduct.itemQtyInCartBeforeSelection >= numberOfItemsToInitiateUpsellPrompt
    ) {
      return false;
    }

    // Return true or false based on the number of tickets to initiate the prompt, and what is in the cart now
    let itemCountInCartForProduct = 0;
    if (cart && cart.products) {
      itemCountInCartForProduct = cart.products.reduce((count, cartProduct) => {
        return cartProduct.name === eventDetails.name ? count + cartProduct.quantity : count;
      }, 0);
    }

    return itemCountInCartForProduct >= numberOfItemsToInitiateUpsellPrompt;
  };

  /**
   * Fetch events to upsell if params are available, or proceed to the cart.
   */
  const getUpsellEventsOrProceed = () => {
    if (!upsell.initialProduct.details?.upsell && (!eventDetails || !eventDetails.upsell)) {
      proceedToCart();
    } else {
      getEvents();
    }
  };

  /**
   * Save the initial product selections, in case the user goes back.
   */
  const saveInitialProductSelections = () => {
    setUpsell({
      ...upsell,
      initialProduct: {
        ...upsell.initialProduct,
        details: eventDetails,
        tiersConfig
      }
    });
  };

  /**
   * Save the prompt product selections, in case the user goes back.
   */
  const savePromptProductSelections = () => {
    const updatedUpsell = { ...upsell };
    if (eventDetails) {
      updatedUpsell.promptProduct.details = eventDetails;
      updatedUpsell.promptProduct.tiersConfig = tiersConfig;
    } else if (membershipProduct) {
      updatedUpsell.promptProduct.membership = membershipProduct;
    }
    setUpsell(updatedUpsell);
  };

  /**
   * Revert to the initial product selections, clearing the details and tiersConfig.
   */
  const revertToInitialProductSelections = () => {
    setEventDetails(upsell.initialProduct.details);
    setEventsConfig(upsell.initialProduct.eventsConfig);
    setTiersConfig(upsell.initialProduct.tiersConfig);
    setUpsell({
      ...upsell,
      initialProduct: {
        ...upsell.initialProduct,
        details: undefined,
        tiersConfig: undefined
      }
    });
  };

  /**
   * Revert to the prompt product selections, clearing the details and tiersConfig.
   */
  const revertToPromptProductSelections = () => {
    setEventsConfig(upsell.promptProduct.eventsConfig);
    if (upsell.promptProduct.details) {
      if (membershipProduct) resetMembershipProduct();
      setEventDetails(upsell.promptProduct.details);
      setTiersConfig(upsell.promptProduct.tiersConfig);
    } else if (upsell.promptProduct.membership) {
      if (eventDetails) resetEventDetails();
      setMembershipProduct(upsell.promptProduct.membership);
    }
    setUpsell({
      ...upsell,
      promptProduct: {
        ...upsell.promptProduct,
        details: undefined,
        tiersConfig: undefined,
        membership: undefined
      }
    });
  };

  /**
   * When going back from this page, all items added to the cart since the last
   * (initial or prompt) product should be removed from the cart, including items
   * for the last (initial or prompt) product.
   *
   * If this would remove all items from the cart, then the cart is released.
   */
  const removeUpsellItemsFromCart = () => {
    if (cart) {
      const itemIdsToKeepInCart: string[] = isPromptProductStored
        ? get(upsell, 'promptProduct.itemIdsInCartBeforeSelection', [])
        : get(upsell, 'initialProduct.itemIdsInCartBeforeSelection', []);

      const removePayload: Item[] = [];
      if (itemIdsToKeepInCart.length) {
        // Build payload of items to remove, which were added since the last (initial or prompt) product
        cart.products.forEach((product) => {
          if (product.items) {
            const itemsToRemove = product.items.filter((i) => !itemIdsToKeepInCart.includes(i.id));
            removePayload.push(...itemsToRemove.map((item) => item.removeParams));
          }
        });
      }

      // Remove select tickets, or release the cart if all tickets are being removed
      if (!removePayload.length || removePayload.length === cart.quantity) {
        releaseCart(cart.releaseParams);
      } else {
        removeTicketsFromCart(removePayload);
      }
    }
  };

  const onPrevPageClick = () => {
    removeUpsellItemsFromCart();
    setNavigateBack(true);
  };

  const proceedToCart = () => {
    navigate('/ticketing/cart', { state: { from: location.pathname } });
  };

  const closeCartErrorOverlay = () => {
    setNavigateBack(false);
    resetCartError();
  };

  /**
   * When the upsell prompt is cancelled, fetch the upsell products or proceed to the cart.
   */
  const onUpsellPromptCancel = () => {
    setUILoading(true);
    setShowUpsellPrompt(false);
    getUpsellEventsOrProceed();
  };

  /**
   * When the upsell prompt is confirmed, select the product to upsell.
   */
  const onUpsellPromptConfirm = () => {
    const productToUpsell = events.find((event) => event.name === upsellPromptProductName) as Event;

    setUpsell({
      ...upsell,
      promptProduct: {
        eventsConfig: { page: currentPage, selected: productToUpsell.id },
        itemIdsInCartBeforeSelection: cartItemIds
      }
    });
    setSelectedProduct(productToUpsell.id);
  };

  /**
   * When a product is selected: navigate to the next page depending on the product type.
   */
  const onNextPageClick = () => {
    if (selectedProduct.length) {
      setEventsConfig({ page: currentPage, selected: selectedProduct });
      setTiersConfig({ selected: '' });
      resetEventDetails();
      resetMembershipProduct();

      // Find the selected product in the upsell products or events (for the prompt product)
      const product = upsellProducts.length
        ? upsellProducts.find((e) => e.id === selectedProduct)
        : events.find((e) => e.id === selectedProduct);

      // Use the selected product type to determine where to navigate to
      let pathname = '/ticketing/tiers';
      if (product && product.type && product.type === 'membership') {
        pathname = '/ticketing/membership-level-selection';
      } else if (product && product.type && product.type === 'donation') {
        pathname = '/ticketing/donation';
      }

      // re-routes to the next page
      navigate(pathname, { state: { from: location.pathname } });
    }
  };

  const getEventImageUrl = (productName: string, imageUrl?: string) => {
    const imgObj =
      productName &&
      eventImages.find((item: MediaResponse) => {
        return item.name === hash(productName.replace(new RegExp('/', 'g'), ' '));
      });
    // image from media table
    if (imgObj && imgObj.s3Name) {
      return previewUrl.concat(imgObj.s3Name);
    }
    // image from ticketing system
    if (imageUrl) {
      return imageUrl;
    }

    return '';
  };

  const getForwardNavButtonText = () => {
    const configuredText = get(messages, `buttons.${i18n.language}[1]`);
    if (configuredText) return configuredText;
    if (cartEnabled) return t('cartSummary.proceedToCart', { cartLabel });

    return t('commonText.next');
  };

  const getDateForDisplay = (product: Event) => {
    const { showDate } = getEventDisplaySettings(eventDisplaySettingsList, product.name);

    let displayDate = '';
    if (showDate) {
      if (product.firstDate && product.lastDate) {
        displayDate = `${t('{{date, weekdayMonthDay}}', { date: new Date(product.firstDate) })} - ${t(
          '{{date, weekdayMonthDay}}',
          {
            date: new Date(product.lastDate)
          }
        )}`;
      } else if (product.date) {
        displayDate = t('{{date, weekdayMonthDay}}', { date: new Date(product.date) });
      }
    }

    return displayDate;
  };

  const getTimeForDisplay = (product: Event) => {
    const { showTime } = getEventDisplaySettings(eventDisplaySettingsList, product.name);

    return showTime && product.date ? t('{{date, time}}', { date: new Date(product.date) }) : '';
  };

  if (uiLoading || cartLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  if (showUpsellPrompt) {
    return (
      <ActionOverlay
        title={upsellPromptHeader}
        description={upsellPromptSubheader}
        confirmText={upsellPromptConfirmButton}
        onConfirm={onUpsellPromptConfirm}
        cancelText={upsellPromptCancelButton}
        onCancel={onUpsellPromptCancel}
      />
    );
  }

  return (
    <>
      <Grid className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          {/* Title */}
          <Grid container item direction="row" xl={6} lg={12} className={classes.headerBlock}>
            <Grid item sx={{ width: 1590 }} className={classes.headerText}>
              <Box flexOverride="none" margin="0 0 5">
                <Typography variant="h2">{header}</Typography>
                <Typography fontSize={30} fontWeight={300}>
                  {subheader}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* This is displayed when there are no products loaded */}
          {!loading && filteredProducts.length === 0 && (
            <Grid container className={classes.noProductsBlock}>
              <MuiBox className={classes.noProductsImage}>
                <Img src={EmptyTicketIcon} width={207} height={180} />
              </MuiBox>
              <Typography fontSize={30} fontWeight={300}>
                {t('events.noEvents')}
              </Typography>
            </Grid>
          )}
          {/* List of product cards, wrapped in paginator. Paginates if there are more than 4 products */}
          {
            <Paginator
              items={filteredProducts}
              itemsPerPage={matchPortrait ? 3 : 4}
              page={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            >
              {({ items }) => (
                <div className={classes.cardContainer}>
                  {items.map((product, idx) => (
                    <MuiBox key={idx} className={classes.cardBox}>
                      {/* Product Card */}
                      <Card
                        hasBorderRadius
                        raised
                        fullHeight
                        hasBorder={product.id === selectedProduct}
                        borderColor={palette?.primary}
                      >
                        {/* Selects product on click of card */}
                        <Card.ActionArea onClick={() => setSelectedProduct(product.id)}>
                          <Grid container className={classes.cardContent}>
                            {todayEventFlag && product.date && isToday(new Date(product.date)) && (
                              <Card.Badge>{t('events.today')}</Card.Badge>
                            )}
                            <Grid item className={classes.cardImage}>
                              <Img
                                src={[getEventImageUrl(product.name, product.imageUrl), `${previewUrl}default.png`]}
                                className={classes.image}
                                unloader={<img src={WillCallLogo} alt="Event Logo" className={classes.defaultImage} />}
                              />
                            </Grid>
                            <Grid item>
                              <MuiBox className={classes.horizontalDivider}>
                                <Card.Divider />
                              </MuiBox>
                              <MuiBox className={classes.verticalDivider} />
                            </Grid>
                            <Grid container item className={classes.cardText} direction="column">
                              <FlexBox direction="column" justify="center" align="start">
                                <Typography fontSize={28} fontWeight={400}>
                                  {product.alias || product.name}
                                </Typography>
                              </FlexBox>
                              <FlexBox direction="column" justify="between" align="start">
                                <Typography fontSize={24} fontWeight={300}>
                                  {getDateForDisplay(product) || <>&nbsp;</>}
                                </Typography>
                                <Typography fontSize={24} fontWeight={300}>
                                  {getTimeForDisplay(product) || <>&nbsp;</>}
                                </Typography>
                              </FlexBox>
                            </Grid>
                          </Grid>
                        </Card.ActionArea>
                      </Card>
                    </MuiBox>
                  ))}
                </div>
              )}
            </Paginator>
          }
        </Grid>
      </Grid>
      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        onNextClick={proceedToCart}
        nextText={getForwardNavButtonText()}
        prevText={backButton}
        isNextDisabled={!cart}
      />

      {/* Error overlay if the remove API fails */}
      {cartError && (
        <ActionOverlay
          title={
            <MuiBox className={classes.cartError}>
              <Typography fontSize={48} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {t('cart.unableToRemove', { cartLabel })}
              </Typography>
            </MuiBox>
          }
          height={500}
          confirmText={t('commonText.tryAgain')}
          onConfirm={() => closeCartErrorOverlay()}
          style={{ zIndex: '2147483648' }}
        />
      )}
    </>
  );
};

export default UpsellSelectionView;
